import { StyleSheet } from '@react-pdf/renderer';
import { FundPerformanceMetric } from 'further-types/lp/period-end-report';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { FC } from 'react';
import Column from '../Pdf/components/Column';
import PdfAccountingTable from '../Pdf/components/PdfAccountingTable';
import theme from '../Pdf/components/theme';
import PdfLabelColumnHeader from './components/pdf/LabelColumnHeader';
import PdfLabelWithSecondary from './components/pdf/LabelWithSecondary';
import PeriodEndReportPage from './components/pdf/PeriodEndReportPage';
import PdfPeriodText from './components/pdf/PeriodText';
import SectionTitle from './components/pdf/SectionTitle';
import SubsectionTitle from './components/pdf/SubsectionTitle';
import { buildFundPerformanceMetricsTableSections } from './helpers/fund-performance-metrics-table';
import { PeriodEndReportSectionPageProps } from './types';

const numberToWord: Record<number, string> = {
  2: 'two',
  3: 'three',
  4: 'four',
};

const styles = StyleSheet.create({
  tables: {
    paddingTop: theme.spacing(theme.spacingSizes.xs),
    gap: '12px',
  },
  singlePeriodTables: {
    paddingTop: theme.spacing(theme.spacingSizes.xs),
    gap: '12px',
    width: '400px',
    alignSelf: 'center',
  },
  labelHeaderColumn: {
    paddingLeft: '10px',
  },
  headerColumn: {
    paddingLeft: '8px',
  },
});

type Props = PeriodEndReportSectionPageProps & {
  vehicleName: string;
  baseCurrency: CurrencyCode;
  fundPerformanceMetrics: Array<FundPerformanceMetric>;
  hasFundValueBridge: boolean;
  hasFundValueProgression: boolean;
};

const FundPerformanceMetrics: FC<Props> = ({
  vehicleName,
  baseCurrency,
  fundPerformanceMetrics,
  hasFundValueBridge,
  hasFundValueProgression,
  sectionNumber,
  ...rest
}) => {
  const displaySectionTitle = !hasFundValueBridge && !hasFundValueProgression;
  const subsectionNumber =
    [hasFundValueBridge, hasFundValueProgression].filter(Boolean).length + 1;
  const periodCount = fundPerformanceMetrics.length;
  const hasSinglePeriod = periodCount === 1;
  const subtitle = hasSinglePeriod
    ? 'Last period'
    : `Last ${numberToWord[periodCount] ?? periodCount} periods`;

  const columnHeaders = [
    {
      key: 'label',
      label: (
        <Column style={styles.labelHeaderColumn}>
          <PdfLabelColumnHeader title={vehicleName} subtitle={subtitle} />
        </Column>
      ),
      weight: hasSinglePeriod ? 1 : 280,
      alignTop: true,
    },
    ...fundPerformanceMetrics.map(({ periodEnd }) => ({
      key: periodEnd.toString(),
      label: (
        <Column style={styles.headerColumn}>
          <PdfPeriodText>{periodEnd.toReadable()}</PdfPeriodText>
        </Column>
      ),
      weight: hasSinglePeriod ? 1 : 150,
      alignBottom: true,
    })),
  ];
  const sections = buildFundPerformanceMetricsTableSections({
    fundPerformanceMetrics,
    baseCurrency,
  });

  return (
    <PeriodEndReportPage
      {...rest}
      header={`0${sectionNumber} – Fund performance summary`}
    >
      {displaySectionTitle ? (
        <SectionTitle
          sectionNumber={sectionNumber}
          title="Fund performance summary"
        />
      ) : null}
      <SubsectionTitle
        sectionNumber={sectionNumber}
        subsectionNumber={subsectionNumber}
        title="Fund status breakdown"
        paddedTop={displaySectionTitle}
      />
      <Column
        fullWidth={!hasSinglePeriod}
        style={hasSinglePeriod ? styles.singlePeriodTables : styles.tables}
      >
        {sections.map((section, index) => (
          <PdfAccountingTable
            key={section.id}
            columnHeaders={columnHeaders}
            rows={[
              {
                id: `${section.id}-title`,
                singleCell: true,
                paddingY: 6,
                paddingX: 24,
                indentation: 12,
                backgroundColor: theme.palette.grey[50],
                fontSize: 6,
                lineHeight: 6,
                cells: [section.title],
              },
              ...section.rows.map((row) => {
                const isRecallableDistributions =
                  row.id === 'cumulative-recallable-distributions';

                return {
                  id: row.id,
                  indentation: isRecallableDistributions ? 20 : 12,
                  paddingY: 4,
                  color: isRecallableDistributions
                    ? theme.palette.grey[700]
                    : undefined,
                  fontWeight: 500,
                  fontSize: 5.5,
                  lineHeight: 6,
                  cellLeftBorders: true,
                  cells: [
                    row.secondary ? (
                      <PdfLabelWithSecondary
                        key={row.id}
                        label={row.label}
                        secondary={row.secondary}
                        fontSize={5.5}
                      />
                    ) : (
                      row.label
                    ),
                    ...row.values,
                  ],
                };
              }),
            ]}
            rounded
            headerVisible={index === 0}
            headerPaddingY={6}
            headerPaddingX={0}
          />
        ))}
      </Column>
    </PeriodEndReportPage>
  );
};

export default FundPerformanceMetrics;
